import React from 'react';

import { cn } from '@/lib/utils';

/**
 * F42 UNAVAILABLE-NOT-ZERO-1: the one failed / unknown state for a number or a
 * block of data. A fetch that failed, or a value the server did not send, is
 * NOT zero and NOT "nothing here yet": it renders this, in a neutral tone —
 * never green, never a count. "No data yet" is only for a successful fetch
 * that returned no rows.
 */
export const UNAVAILABLE_TEXT = 'Unavailable';

/** Inline form, for a stat value. */
export function UnavailableValue({
  className,
  testId = 'unavailable-value',
  title,
}: {
  className?: string;
  testId?: string;
  title?: string;
}): React.ReactElement {
  return (
    <span
      className={cn('text-slate-500 dark:text-slate-400', className)}
      data-testid={testId}
      title={title}
    >
      {UNAVAILABLE_TEXT}
    </span>
  );
}

/**
 * Block form, for a card or panel body. `onRetry` is passed only where the
 * surface has a real reload to call; without it no retry control is drawn.
 */
export function UnavailablePanel({
  onRetry,
  retrying = false,
  detail,
  className,
  testId = 'unavailable-panel',
}: {
  onRetry?: () => void;
  retrying?: boolean;
  detail?: string;
  className?: string;
  testId?: string;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'flex min-h-[100px] flex-col items-center justify-center gap-2 text-center',
        className,
      )}
      role="status"
      data-testid={testId}
    >
      <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{UNAVAILABLE_TEXT}</p>
      {detail ? <p className="text-xs text-slate-500 dark:text-slate-400">{detail}</p> : null}
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="text-xs font-medium text-slate-700 underline hover:text-slate-900 disabled:opacity-50 dark:text-slate-300"
        >
          {retrying ? 'Retrying…' : 'Try again'}
        </button>
      ) : null}
    </div>
  );
}
